(ns frontend.view.modals.survey-cycle-actions.view
  (:require
   [ajax.core :as ajax]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.languages :as languages]
   [common.util :as cu]
   [common.localization :as loc]
   [common.schema.entities.reminder-rule.options :as reminder-rule-options]
   [common.opensearch.fields :as cos]
   [frontend.conn :as conn]
   [frontend.state.current-session :as session]
   [frontend.updates.entity-scope :as entity-scope]
   [promesa.core :as p]
   [ui.components.alert.view :as alert]
   [ui.components.badge.view :as badge]
   [ui.components.button.view :as button]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.modal.view :as modal]
   [ui.components.radio-group.view :as radio-group]
   [ui.components.tab-strip.view :as tab-strip]
   [ui.components.text.view :as text]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce modal-key :survey-cycle-actions-modal)

(defn kw-label
  [kw]
  (some-> kw name (string/replace "-" " ") (string/replace "." " ") (string/replace "/" " ")))

(defn maybe-entity
  [entity-or-id]
  (if (:db/id entity-or-id)
    entity-or-id
    (session/get-entity entity-or-id)))

(defn target-display-label
  "The search endpoint pulls survey profile targets as bare refs, so resolve
   the full entity from the client session for a readable display string."
  [survey-profile]
  (let [target (:survey-profile/target survey-profile)
        target-id (or (:db/id target) target)
        resolved (or (some-> target-id session/get-entity)
                     target)
        label (entity/display-string resolved)]
    (if (string? label)
      label
      (str label))))

(defn survey-batches-for-cycle
  [survey-cycle]
  (->> (:survey-cycle/survey-batches survey-cycle)
       (map maybe-entity)
       (remove nil?)
       (remove :common/disabled?)
       (sort-by :survey-batch/batch-label)
       vec))

(defn default-batch-label
  "What a new batch is called before anyone renames it.

   Numbered from the second one on. Every batch in a cycle used to be
   offered the same name - the cycle's - and most people keep the default,
   so a cycle with three batches showed three identical rows that could only
   be told apart by their summary line."
  [survey-cycle existing-batches]
  (let [base (str (:survey-cycle/cycle-label survey-cycle) " batch")
        n (count existing-batches)]
    (if (pos? n)
      (str base " " (inc n))
      base)))

(defn containing-entity-id
  [entity]
  (or (get-in entity [:common/containing-entity :db/id])
      (:common/containing-entity entity)))

(defn survey-type-id-for-cycle
  "Survey cycles link to their survey type through :survey-type/survey-cycles,
   so resolve the type by reverse lookup with :common/containing-entity as a
   fallback for legacy data."
  [survey-cycle]
  (let [cycle-id (:db/id survey-cycle)]
    (or (->> @session/entity-types
             :entity.type/survey-type
             (remove :common/disabled?)
             (filter (fn [survey-type]
                       (->> (:survey-type/survey-cycles survey-type)
                            (some (fn [cycle-ref]
                                    (= cycle-id (or (:db/id cycle-ref) cycle-ref)))))))
             first
             :db/id)
        (containing-entity-id survey-cycle))))

(def profile-page-size
  "`backend.domain.search.universal/bounded-search-size` caps a page at 200, and
   the handler does not accept a size, so this mirrors its ceiling rather than
   pretending to choose one."
  200)

(defn search-profiles!
  "One page of the survey type's profiles, from the universal index.

   The previous implementation asked Datomic for every profile of the survey
   type in one unpaged query and rendered a checkbox per row, which does not
   survive a tenant with thousands of them.

   A new search replaces the results, and with them the ticks made in the last
   one. What survives a search is the recipient list, which is built by adding
   to it."
  [{:keys [state survey-type-id query from-index append?]}]
  (when survey-type-id
    (swap! state assoc :profiles-loading? true)
    (-> (conn/sendp! :search/universal-search
                     {:query        (or query "")
                      :entity-types [:entity.type/survey-profile]
                      :from-index   (or from-index 0)
                      :sort-terms   [{"extras.lower-case-display-name.keyword" {:order "asc"}}]
                      :filter-terms {"extras.survey-type-id" survey-type-id}})
        (p/then
         (fn [{:keys [body]}]
           (let [response (-> body :responses first)
                 total (get-in response [:hits :total :value])
                 hits (get-in response [:hits :hits])
                 items (mapv (fn [hit]
                               {:db/id (-> hit :_id (js/parseInt))
                                :label (cos/hit->entity-name hit)})
                             hits)]
             (swap! state
                    (fn [current]
                      (let [existing (if append? (vec (:candidate-profiles current)) [])]
                        (cond-> current
                          true
                          (assoc :profiles-loading? false
                                 :profile-total total
                                 :profile-from (+ (or from-index 0) (count items))
                                 :candidate-profiles (into existing items))

                          ;; An unfiltered search is the only one that reports
                          ;; how many profiles the survey type actually has,
                          ;; which is what selecting "all of this type" covers -
                          ;; a filtered total would understate it.
                          (string/blank? query)
                          (assoc :profile-total-all total))))))))
        (p/catch
         (fn [err]
           (swap! state assoc
                  :profiles-loading? false
                  :error (str (loc/tr :survey-cycle-profile-load-failed) " " err)))))))

(defn staged-profiles
  "The rows ticked in the current search result, as {:db/id :label} hits.

   Read back off the loaded page rather than kept as maps, so a tick can never
   outlive the result it was made in."
  [state candidate-profiles]
  (let [staged (or (:staged-profile-ids @state) #{})]
    (->> candidate-profiles
         (filter (fn [survey-profile]
                   (contains? staged (:db/id survey-profile))))
         (vec))))

(defn request!
  "Call a registered endpoint through the HTTP /rpc transport."
  [state operation body on-success]
  (swap! state assoc :processing? true :error nil :success nil)
  (ajax/POST "/rpc"
    {:params          {:operation operation
                       :body      body}
     :format          (ajax/transit-request-format)
     :response-format (ajax/transit-response-format)
     :handler         (fn [response]
                        (swap! state assoc :processing? false)
                        (on-success (:body response)))
     :error-handler   (fn [err]
                        (swap! state assoc
                               :processing? false
                               :error (or (get-in err [:response :message])
                                          (:status-text err)
                                          (loc/tr :failed))))}))

(defn default-sendable-cycle
  [survey-cycles]
  (or (->> survey-cycles
           (filter (fn [survey-cycle]
                     (= :survey-cycle.status/active
                        (:survey-cycle/cycle-status survey-cycle))))
           (first))
      (first survey-cycles)))

(defn reminder-scope
  "What the reminder endpoints are asked about.

   Opened from the survey type listing the row selection is the point - the
   question is what happens to those profiles - so it is sent and the preview
   comes back narrowed, keeping the ones with nothing due so they can be shown
   as such. Opened from a cycle there is no selection and the whole batch is
   the subject.

   The same scope goes to the preview, to scheduling and to sending. Sending
   a wider set than the screen listed is the failure this avoids."
  [{:keys [survey-cycle-id survey-batch-id batch-recipients]}]
  (cond-> {:survey-cycle-id survey-cycle-id}
    survey-batch-id
    (assoc :survey-batch-id survey-batch-id)

    (seq batch-recipients)
    (assoc :profile-ids (mapv :db/id batch-recipients))))

(defn hydrate-cycle!
  "Pulls a chosen cycle into `@session/entities`.

   `get-sendable-cycles` returns a four-key summary - enough to label the
   picker and nothing else. Everything below this point reads the cycle as a
   session entity: its batches, its automatic-reminders flag, the survey type
   behind it. Opened from settings that entity is already there, because the
   settings page holds a subscription rooted at the survey type. Opened from
   the listing nobody has loaded it, so fetch it once before letting the rest
   of the screen read it.

   `fetch-once!` rather than a live subscription: a modal has no lifecycle
   hook to close one on, and nothing here needs to react to a cycle changing
   underneath it."
  [{:keys [state survey-cycle-id on-ready]}]
  (when (int? survey-cycle-id)
    (swap! state assoc :hydrating-cycle? true)
    (entity-scope/fetch-once!
     {:entity-id survey-cycle-id
      :on-done   (fn [_entities]
                   (swap! state assoc :hydrating-cycle? false)
                   (when on-ready (on-ready)))
      :on-error  (fn [error]
                   (swap! state assoc
                          :hydrating-cycle? false
                          :error (str error)))})))

(defn load-sendable-cycles!
  "Opened from the survey type listing there is no cycle yet - the profiles
   came from the grid selection and the cycle is the thing still to choose.
   Opened from a cycle row in settings this never runs, because that cycle is
   fixed and arrives in the props."
  [{:keys [state survey-type-id on-chosen]}]
  (when survey-type-id
    (request! state
              :survey-cycle/get-sendable-cycles
              {:survey-type-id survey-type-id}
              (fn [body]
                (let [survey-cycles (vec body)
                      survey-cycle (default-sendable-cycle survey-cycles)]
                  (swap! state assoc
                         :sendable-cycles survey-cycles
                         :chosen-cycle-id (:db/id survey-cycle))
                  (when survey-cycle
                    ;; `on-chosen` runs after hydration, not beside it. The
                    ;; summary this picked from carries four keys and no
                    ;; batches, so anything that has to count them - naming
                    ;; the next batch, for one - can only be right once the
                    ;; cycle itself is in the session.
                    (hydrate-cycle! {:state           state
                                     :survey-cycle-id (:db/id survey-cycle)
                                     :on-ready        (fn []
                                                        (when on-chosen
                                                          (on-chosen
                                                           (or (session/get-entity
                                                                (:db/id survey-cycle))
                                                               survey-cycle))))})))))))

(defn sendable-cycle-options
  [survey-cycles]
  (->> survey-cycles
       (mapv (fn [survey-cycle]
               {:value (:db/id survey-cycle)
                :label (:survey-cycle/cycle-label survey-cycle)}))))

(defn selected-batch
  [survey-batches selected-batch-id]
  (some (fn [survey-batch]
          (when (= selected-batch-id (:db/id survey-batch))
            survey-batch))
        survey-batches))

(defn batch-sent?
  [survey-batch]
  (contains? #{:survey-batch.status/sent
               :survey-batch.status/closed}
             (:survey-batch/batch-status survey-batch)))

(defn select-batch!
  "Point the modal at a batch.

   A sent batch loads its own contents straight away. They are a record of what
   went out rather than a preview of what might, so there is nothing for the
   user to ask for and nothing to refresh. Anything previewed belongs to the
   previous selection, so it goes."
  [{:keys [state survey-cycle-id survey-batches survey-batch-id]}]
  (swap! state assoc
         :selected-batch-id survey-batch-id
         :preview-items nil
         :success nil)
  (when (batch-sent? (selected-batch survey-batches survey-batch-id))
    (request! state
              :survey-cycle/get-batch-send-data
              ;; No profile ids: the batch names its own members, and the set a
              ;; sent batch went to is not the set the picker happens to be
              ;; filtered to.
              {:survey-cycle-id survey-cycle-id
               :survey-batch-id survey-batch-id
               :profile-ids     []}
              (fn [body]
                (swap! state assoc :preview-items body)))))

(defn ensure-initialized!
  "Two ways in, and they differ only in which of the two inputs is already
   known. From a cycle row in settings the cycle is fixed and the recipients
   are still to be searched for; from the survey type listing the recipients
   came from the grid selection and the cycle is still to be chosen. Anything
   after that point is the same flow, which is why there is one screen rather
   than two."
  [{:keys [state survey-cycle survey-type-id seed-profiles active-tab]}]
  (let [init-key [(:db/id survey-cycle) (mapv :db/id seed-profiles) active-tab]]
    (when (not= (:initialized-key @state) init-key)
      (swap! state assoc
             :initialized-key init-key
             :initialized-cycle-id (:db/id survey-cycle)
             :active-tab (or active-tab :invitations)
             :candidate-profiles nil
             :batch-label (when survey-cycle
                            (default-batch-label survey-cycle
                                                 (survey-batches-for-cycle survey-cycle)))
             :batch-deadline-override nil
             :include-cc? true
             :preview-items nil
             :initialized-batch-id nil
             :selected-batch-id nil
             :due-reminder-items nil
             :processing? false
             :error nil
             :success nil
             :profile-query nil
             :profile-total nil
             :profile-from 0
             :sendable-cycles nil
             :chosen-cycle-id nil
             ;; Arriving from the listing there is nothing to look at but the
             ;; batch being composed, so skip straight to composing it.
             :creating-batch? (nil? survey-cycle)
             ;; Who the batch will be sent to, accumulated across as many searches
             ;; as it takes. The batch is posted as exactly these ids. A grid
             ;; selection seeds it; searching adds to it the same way.
             ;;
             ;; The rows themselves, not their ids: grid rows are fetched by
             ;; the grid and never reach the session cache, so there is
             ;; nothing to resolve an id against here. Reduced to the same
             ;; {:db/id :label} shape the search produces, because that is
             ;; what the recipient list renders - a raw grid row has no
             ;; :label and came out as a blank row with a remove button.
             :batch-recipients (mapv (fn [survey-profile]
                                       {:db/id (:db/id survey-profile)
                                        :label (or (:label survey-profile)
                                                   (target-display-label survey-profile))})
                                     seed-profiles)
             ;; Ticks on the current result page only. Adding moves them into
             ;; :batch-recipients and clears them; a new search discards them.
             :staged-profile-ids #{})
      (search-profiles! {:state          state
                         :survey-type-id survey-type-id
                         :query          nil
                         :from-index     0})
      (if survey-cycle
        ;; Pick the default batch here rather than letting the render fall back to
        ;; the first option, so every selection - the first one included - goes
        ;; through select-batch! and a sent batch arrives with its record loaded.
        (let [survey-batches (survey-batches-for-cycle survey-cycle)]
          (when-let [survey-batch (first survey-batches)]
            (select-batch! {:state           state
                            :survey-cycle-id (:db/id survey-cycle)
                            :survey-batches  survey-batches
                            :survey-batch-id (:db/id survey-batch)})))
        (load-sendable-cycles!
         {:state          state
          :survey-type-id survey-type-id
          :on-chosen      (fn [chosen]
                            (swap! state assoc
                                   :batch-label (default-batch-label
                                                 chosen
                                                 (survey-batches-for-cycle chosen))))})))))

(defn recipient-ids
  [state]
  (->> (:batch-recipients @state)
       (map :db/id)
       (set)))

(defn toggle-staged-profile!
  "Tick a row of the current search result.

   Ticks are per-search scratch, not the recipient list. Searching again
   replaces the results and these ticks with it; only Add puts anything in the
   list that the batch is built from."
  [state profile-id checked?]
  (swap! state update
         :staged-profile-ids
         (fn [staged]
           (let [staged (or staged #{})]
             (if checked?
               (conj staged profile-id)
               (disj staged profile-id))))))

(defn add-recipients!
  "Append profiles to the recipient list, skipping any already in it.

   Order is the order they were added, so the list reads as a record of what
   the user did rather than being reshuffled by each addition."
  [state survey-profiles]
  (swap! state
         (fn [current]
           (let [existing (->> (:batch-recipients current)
                               (map :db/id)
                               (set))
                 additions (->> survey-profiles
                                (remove (fn [survey-profile]
                                          (contains? existing (:db/id survey-profile))))
                                (cu/distinct-by :db/id)
                                (mapv (fn [survey-profile]
                                        {:db/id (:db/id survey-profile)
                                         :label (:label survey-profile)})))]
             (-> current
                 (update :batch-recipients (fnil into []) additions)
                 (assoc :staged-profile-ids #{}))))))

(defn remove-recipient!
  [state profile-id]
  (swap! state update
         :batch-recipients
         (fn [recipients]
           (->> recipients
                (remove (fn [recipient] (= profile-id (:db/id recipient))))
                (vec)))))

(defn clear-recipients!
  [state]
  (swap! state assoc
         :batch-recipients []
         :staged-profile-ids #{}))

(defn profile-row
  "A row of the paged search result. `survey-profile` is a search hit
   (`{:db/id :label}`), not a full entity - the picker never loads the whole
   profile now."
  [{:keys [state survey-profile added?]}]
  (let [profile-id (:db/id survey-profile)
        staged? (contains? (:staged-profile-ids @state) profile-id)]
    [:tr
     {:key (str "survey-profile-cycle-action-" profile-id)
      :class "*:px-3 *:py-2 *:text-sm"}
     [:td
      {:class "w-10"}
      ;; A profile already in the list is shown rather than hidden. Dropping it
      ;; from the results would leave "why is this person not in my search?"
      ;; unanswerable, and the answer is the useful part.
      (when-not added?
        [checkbox/view
         {:value staged?
          :style-variant :tw2
          :on-change (fn [value]
                       (toggle-staged-profile! state profile-id value))}])]
     [:td
      [:div
       {:class "flex items-center gap-2"}
       [:div
        {:class (if added?
                  "font-medium text-base-light-text-lighter"
                  "font-medium")}
        (:label survey-profile)]
       (when added?
         [badge/neutral {:label (loc/tr :survey-cycle-actions/already-added)}])]]]))

(defn numbered-section
  "One step of the flow: a number, a title, an optional right-aligned control,
   and the step's own content directly beneath it.

   The three steps are shown at once rather than as a wizard, because the whole
   point of the middle step is to be readable while deciding whether to press
   the button in the last one."
  [{:keys [index title action]} & children]
  [:div
   {:class "flex flex-col gap-2"}
   [:div
    {:class "flex items-center justify-between gap-3"}
    [:div
     {:class "flex items-center gap-2"}
     [:span
      {:class "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-base-light-border-paler text-xs font-semibold"}
      index]
     [:div {:class "text-sm font-semibold"} title]]
    action]
   [:div {:class "pl-8"} (into [:<>] children)]])

(defn status-badge
  [mass-send-status]
  (let [label (kw-label mass-send-status)]
    (case mass-send-status
      :survey-cycle.mass-send-status/ready [badge/success {:label label}]
      :survey-cycle.mass-send-status/skip  [badge/warning {:label label}]
      (nil "")                             [:span]
      [badge/danger {:label label}])))

(defn recipients-cell
  "A row with no recipients still reads as ready and sends nothing, so it is
   called out rather than shown as a bare zero."
  [recipients]
  (cond
    (nil? recipients) [:span {:class "text-xs text-base-light-text-lighter"} "-"]
    (zero? recipients) [badge/danger {:label (loc/tr :survey-cycle-actions/no-recipients)}]
    :else [:span {:class "text-xs"} (str recipients)]))

(defn table-shell
  "The component library has no plain table primitive; the surrounding survey
   views hand-roll one the same way."
  [headers rows]
  [:div
   {:class "border border-solid border-base-light-border-paler rounded-lg overflow-hidden"}
   [:table
    {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
    [:thead
     {:class "bg-base-lightest"}
     (into [:tr]
           (map (fn [header]
                  [:th
                   {:key   (str "th-" header)
                    :class "px-3 py-2 text-left text-xs font-semibold text-base-light-text-lighter"}
                   header])
                headers))]
    (into [:tbody {:class "divide-y divide-solid divide-base-light-border-paler"}]
          rows)]])

(defn preview-row
  [item]
  ;; `:batch-process` when the preview describes an existing batch, `:new-process`
  ;; when it describes the batch that would be created. Never both.
  (let [process (or (:batch-process item) (:new-process item))
        recipients (:invitee-count item)]
    [:tr
     {:key (str "cycle-preview-" (:db/id item))
      :class "*:px-3 *:py-2 *:text-sm"}
     [:td
      [:div {:class "font-medium"} (get-in item [:target :label])]
      [:div {:class "pt-1"} [status-badge (:survey-cycle/mass-send-status item)]]]
     [:td
      {:class "text-xs"}
      (or (get-in process [:survey-process/template :label]) "-")]
     [:td
      {:class "text-xs whitespace-nowrap"}
      (or (some-> process :survey-process/deadline js/Date. .toLocaleDateString) "-")]
     [:td
      {:class "whitespace-nowrap"}
      [recipients-cell recipients]]
     [:td
      {:class "text-xs text-base-light-text-lighter"}
      (or (:survey-cycle/mass-send-reason item) "-")]]))

(defn process-status-badge
  "Most process statuses are ordinary progress, so they get the neutral badge.
   Only two are worth colouring: completed is the finish line, and a process
   still sitting at template/prefilled inside a sent batch did not go out."
  [status]
  (let [label (kw-label status)]
    (case status
      nil                              [:span]
      :survey-process.status/completed [badge/success {:label label}]
      :survey-process.status/template  [badge/warning {:label label}]
      :survey-process.status/prefilled [badge/warning {:label label}]
      [badge/neutral {:label label}])))

(defn record-row
  "One profile in a batch that has already been sent.

   Deliberately not `preview-row`. A preview answers \"what will this send?\",
   and for a sent batch the answer is always \"nothing\" - reporting every row
   as skipped, with a reason explaining that it will not be sent again, says
   nothing about the batch. What is worth knowing is what each process is now."
  [item]
  (let [process (:batch-process item)]
    [:tr
     {:key (str "cycle-record-" (:db/id item))
      :class "*:px-3 *:py-2 *:text-sm"}
     [:td
      [:div {:class "font-medium"} (get-in item [:target :label])]]
     [:td
      {:class "text-xs"}
      (or (get-in process [:survey-process/template :label]) "-")]
     [:td
      {:class "text-xs whitespace-nowrap"}
      (or (some-> process :survey-process/deadline js/Date. .toLocaleDateString) "-")]
     [:td
      {:class "whitespace-nowrap"}
      [recipients-cell (:invitee-count item)]]
     [:td
      {:class "whitespace-nowrap"}
      [process-status-badge (:survey-process/status process)]]]))

(defn due-reminder-row
  [item]
  [:tr
   {:key (str "due-reminder-" (:db/id item))
    :class "*:px-3 *:py-2 *:text-sm"}
   [:td
    [:div
     {:class "flex items-center gap-2"}
     [:div {:class "font-medium"} (get-in item [:target :label])]
     ;; Selected, but with no process in this batch - so nothing will reach
     ;; them whatever the schedule says. Said out loud, because the row would
     ;; otherwise read the same as one that simply has nothing due.
     (when (false? (:in-batch? item))
       [badge/neutral
        {:label (loc/tr :survey-cycle-actions/not-in-batch)}])]
    [:div {:class "text-xs text-base-light-text-lighter"}
     (or (some-> (:survey-process/deadline item) js/Date. .toLocaleDateString)
         (loc/tr :survey-cycle-actions/no-deadline))]]
   [:td
    {:class "text-xs"}
    ;; The rule's label is free text and can say anything - QA has a "Day of
    ;; deadline" that fires two days early. The schedule it will actually use is
    ;; printed under it, so the decision to send is not taken on the label's
    ;; word alone.
    (if (seq (:survey-reminder/due-reminders item))
      (into [:div {:class "flex flex-col gap-1"}]
            (map (fn [due-reminder]
                   [:div
                    {:key (str "due-reminder-rule-"
                               (get-in due-reminder [:survey-reminder/reminder-rule :db/id]))}
                    [:div
                     {:class "flex items-center gap-2"}
                     [:span (get-in due-reminder [:survey-reminder/reminder-rule :rule-label])]
                     ;; Scheduled earlier and still waiting. Visible because
                     ;; the send is limited to this list - and because only the
                     ;; last of them is actually sent.
                     (when (:survey-reminder/already-scheduled? due-reminder)
                       [badge/neutral
                        {:label (loc/tr :survey-cycle-actions/already-scheduled)}])
                     (when (false? (:survey-reminder/will-send? due-reminder))
                       [badge/warning
                        {:label (loc/tr :survey-cycle-actions/will-be-cancelled)}])]
                    [:div
                     {:class "text-base-light-text-lighter"}
                     (str (reminder-rule-options/schedule-text
                           {:reminder-anchor  (:survey-reminder/reminder-anchor due-reminder)
                            :offset-type      (:survey-reminder/offset-type due-reminder)
                            :offset-value     (:survey-reminder/offset-value due-reminder)
                            :offset-direction (:survey-reminder/offset-direction due-reminder)})
                          " · "
                          (loc/tr :survey-cycle-actions/reminder-scheduled-for
                                  [(or (some-> (:survey-reminder/scheduled-at due-reminder)
                                               js/Date.
                                               .toLocaleDateString)
                                       "-")]))]])
                 (:survey-reminder/due-reminders item)))
      "-")]])

(defn summarize-by
  [items key-fn]
  (->> items
       (group-by key-fn)
       (map (fn [[k grouped]]
              {:label (kw-label k)
               :count (count grouped)}))
       (sort-by :label)
       vec))

(defn summary-badges
  "`empty-label` is what no badges means here, which is not always the same
   thing: before the preview has run there is nothing to say, and after it has
   run and found nothing the answer is that nothing is due. Reporting both as
   \"no data yet\" made a finished check look like one that had not happened."
  [{:keys [title summaries empty-label]}]
  [:div
   {:class "flex flex-col gap-2"}
   (when title
     [:div {:class "text-sm font-semibold"} title])
   (if (seq summaries)
     (into [:div {:class "flex flex-wrap gap-2"}]
           (map (fn [{:keys [label count]}]
                  ^{:key (str title "-" label)}
                  [:span
                   {:class "px-2 py-1 rounded-full bg-base-lightest border border-solid border-base-light-border-paler text-xs font-medium"}
                   (str label ": " count)])
                summaries))
     [:div {:class "text-xs text-base-light-text-lighter"} empty-label])])

(defn displayed-batches
  "The cycle's batches, plus one just created that the session has not caught
   up with yet. Without it the batch you have this moment created is missing
   from the list it is supposed to have joined."
  [survey-batches initialized-batch-id]
  (if (and initialized-batch-id
           (not (some (fn [survey-batch]
                        (= initialized-batch-id (:db/id survey-batch)))
                      survey-batches)))
    (vec (conj survey-batches
               {:db/id                    initialized-batch-id
                :survey-batch/batch-label (str "New batch " initialized-batch-id)}))
    (vec survey-batches)))

(def default-reminder-language
  "What a process with no language of its own is written in. Mirrors the
   server, which falls back to :ENG when the process names no language."
  :ENG)

(defn remindable-processes
  "Processes an ad-hoc reminder can reach: those actually out with a
   respondent. Mirrors `process-open-for-reminders?` on the server - a survey
   that has not been sent yet cannot be chased, and one already answered
   should not be."
  [survey-batch]
  (->> (:survey-batch/survey-processes survey-batch)
       (map maybe-entity)
       (remove nil?)
       (remove :common/disabled?)
       (filter (fn [survey-process]
                 (= :survey-process.status/wip
                    (:survey-process/status survey-process))))
       (vec)))

(defn batch-languages
  "The languages the batch's reminders would go out in.

   A reminder is rendered in the recipient's own language, so composing one
   by hand means composing it once per language present. A process that names
   no language still needs a tab - it is written in the default, and treating
   \"no language\" as \"no recipient\" would silently hide it."
  [survey-batch]
  (->> (remindable-processes survey-batch)
       (map (fn [survey-process]
              (or (:survey-process/language survey-process)
                  default-reminder-language)))
       (distinct)
       (sort)
       (vec)))

(defn sendable-processes
  [survey-batch]
  (->> (:survey-batch/survey-processes survey-batch)
       (map maybe-entity)
       (remove nil?)
       (remove :common/disabled?)
       (filter (fn [survey-process]
                 (contains? #{:survey-process.status/template
                              :survey-process.status/prefilled}
                            (:survey-process/status survey-process))))
       vec))

(defn scheduled-reminders
  [survey-batch]
  (->> (:survey-batch/survey-processes survey-batch)
       (map maybe-entity)
       (remove nil?)
       (remove :common/disabled?)
       (mapcat :survey-process/survey-reminders)
       (map maybe-entity)
       (remove nil?)
       (remove :common/disabled?)
       (filter (fn [survey-reminder]
                 (= :survey-reminder.status/scheduled
                    (:survey-reminder/reminder-status survey-reminder))))
       vec))

(defn sent-reminders
  [survey-batch]
  (->> (:survey-batch/survey-processes survey-batch)
       (map maybe-entity)
       (remove nil?)
       (remove :common/disabled?)
       (mapcat :survey-process/survey-reminders)
       (map maybe-entity)
       (remove nil?)
       (remove :common/disabled?)
       (filter (fn [survey-reminder]
                 (= :survey-reminder.status/sent
                    (:survey-reminder/reminder-status survey-reminder))))
       vec))

(defn batch-row-label
  [survey-batch]
  [:div
   {:class "flex flex-col gap-0.5"}
   [:span (:survey-batch/batch-label survey-batch)]
   [:span
    {:class "text-xs font-normal text-base-light-text-paler"}
    ;; Created-on leads, because batches that already share a name - every
    ;; one made before the default was numbered - have nothing else to tell
    ;; them apart.
    (str (or (some-> (:common/created-on survey-batch) js/Date. .toLocaleDateString)
             "")
         " · ")
    (loc/tr :survey-cycle-actions/batch-summary
            [(or (some-> (:survey-batch/sent-at survey-batch) js/Date. .toLocaleString)
                 (loc/tr :survey-cycle-actions/never-sent))
             (count (->> (:survey-batch/survey-processes survey-batch)
                         (map maybe-entity)
                         (remove nil?)
                         (remove :common/disabled?)))
             (count (sendable-processes survey-batch))])]])

(defn batch-list
  "Every batch in the cycle, one selectable at a time, with the new-batch
   action at the end of the list.

   A radio list rather than a dropdown because the batches are the subject of
   this tab, not a setting on it: how many there are, what state each is in
   and which one is being looked at should all be readable without opening
   anything. The previous arrangement - a select, a separate `New batch`
   toggle and a `Create batch` button that appeared elsewhere - made it
   unclear which of the three was acting on what."
  [{:keys [survey-batches selected-batch-id processing? adding?
           can-add-batch? on-select! on-add!]}]
  [:div
   {:class "flex flex-col gap-3 rounded-lg border border-solid border-base-light-border-paler p-3"}
   [text/label (loc/tr :survey-cycle-actions/batch)]
   (if (seq survey-batches)
     [radio-group/view
      {:value     selected-batch-id
       :on-change on-select!
       :options   (mapv (fn [survey-batch]
                          {:value (:db/id survey-batch)
                           :label [batch-row-label survey-batch]})
                        survey-batches)}]
     [text/info-text (loc/tr :survey-cycle-actions/no-batch-yet)])
   [:div
    [button/secondary-hollow
     {:disabled? (or processing? adding? (not can-add-batch?))
      :tooltip   (when-not can-add-batch?
                   (loc/tr :survey-cycle-actions/all-profiles-in-cycle))
      :on-click  on-add!}
     (loc/tr :survey-cycle-actions/add-batch)]]])

(defn view
  [{:keys [_shown? _close-modal! props state _topmost?]}]
  (let [{:keys [survey-cycle-id profiles survey-type-id active-tab]} props
        survey-cycle (session/get-entity survey-cycle-id)
        survey-type-id (or survey-type-id (survey-type-id-for-cycle survey-cycle))]
    (ensure-initialized! {:state          state
                          :survey-cycle   survey-cycle
                          :survey-type-id survey-type-id
                          :seed-profiles  profiles
                          :active-tab     active-tab})
    (fn [{:keys [shown? close-modal! props state topmost?]}]
      (let [{props-cycle-id :survey-cycle-id} props
            ;; Fixed when the modal was opened from a cycle, chosen in the
            ;; picker when it was opened from the listing. Bound here rather
            ;; than at each use, because every request below sends it and one
            ;; of them reading the props value would send nil from the
            ;; listing.
            survey-cycle-id (or props-cycle-id (:chosen-cycle-id @state))
            survey-cycle (session/get-entity survey-cycle-id)
            cycle-fixed? (some? props-cycle-id)
            candidate-profiles (or (:candidate-profiles @state) [])
            survey-batches (survey-batches-for-cycle survey-cycle)
            batch-rows (displayed-batches survey-batches (:initialized-batch-id @state))
            processing? (:processing? @state)
            profile-total (:profile-total @state)
            batch-recipients (or (:batch-recipients @state) [])
            recipient-ids* (recipient-ids state)
            staged-profiles* (staged-profiles state candidate-profiles)
            ;; Rows this search turned up that are not in the list yet. The Add
            ;; all button counts these rather than the search total, so it never
            ;; promises to add people who are already in.
            addable-profiles (->> candidate-profiles
                                  (remove (fn [survey-profile]
                                            (contains? recipient-ids* (:db/id survey-profile)))))
            ;; Only claim the search is exhausted when every match is loaded.
            ;; With more pages behind it, an unloaded row may still be missing
            ;; from the list, so Add all still has something to do.
            all-matches-loaded? (>= (count candidate-profiles) (or profile-total 0))
            search-fully-added? (and all-matches-loaded?
                                     (seq candidate-profiles)
                                     (empty? addable-profiles))
            selected-batch-id (or (:selected-batch-id @state)
                                  (some-> batch-rows first :db/id))
            selected-batch* (selected-batch survey-batches selected-batch-id)
            ;; Sending a batch happens once and cannot be amended or repeated,
            ;; so a sent batch is a record of what went out, not a thing to
            ;; edit. Its contents stay readable; everything that implies another
            ;; send - choosing profiles, previewing what "will" be sent, the
            ;; send button itself - is not offered at all.
            sent? (batch-sent? selected-batch*)
            creating? (:creating-batch? @state)
            ;; The record stands in for the working sections only while the
            ;; user is looking at the sent batch. Composing a new one still
            ;; needs the picker, or there is no way to say who it goes to.
            record-only? (and sent? (not creating?))
            automatic-reminders? (:survey-cycle/automatic-reminders? survey-cycle)
            ;; A selected batch can describe itself - an empty id list against a
            ;; batch means "everything in it" - so the preview is available
            ;; whether or not anything is in the recipient list.
            can-preview? (some? selected-batch-id)
            can-initialize? (and (some? survey-cycle)
                                 (seq batch-recipients)
                                 (not (string/blank? (:batch-label @state))))
            can-send-batch? (and (some? selected-batch-id)
                                 (pos? (count (sendable-processes selected-batch*))))
            preview-summary (summarize-by (:preview-items @state) :survey-cycle/mass-send-status)
            ;; Reminders, not profiles. A profile can be owed more than one -
            ;; after a previous-reminder chain resolves it usually is - and the
            ;; number that matters is how many emails Send will produce.
            due-reminders* (->> (:due-reminder-items @state)
                                (mapcat :survey-reminder/due-reminders))
            ;; Only one reminder per process is sent; the rest are cancelled.
            ;; The warning has to count what goes out, not what is listed.
            sending-reminders (filter :survey-reminder/will-send? due-reminders*)
            superseded-reminders (remove :survey-reminder/will-send? due-reminders*)
            due-reminder-count (count sending-reminders)
            superseded-count (count superseded-reminders)
            ;; Already-scheduled reminders are listed so they can be sent, but
            ;; scheduling again would create nothing.
            schedulable-count (->> due-reminders*
                                   (remove :survey-reminder/already-scheduled?)
                                   (count))
            ;; nil until the preview has run; an empty vector afterwards means
            ;; it ran and found nothing, which is a different thing to say.
            reminders-checked? (some? (:due-reminder-items @state))
            ;; A profile already in this cycle cannot go into another batch
            ;; of it - `cycle-mass-send-data` reports it as already-in-cycle
            ;; and initialize-batch keeps only ready rows - so once every
            ;; profile of the survey type is in, a new batch could only come
            ;; out empty.
            ;;
            ;; Counted as processes rather than by resolving each one back to
            ;; its profile: the cycle allows a profile at most one process,
            ;; which is the rule that makes already-in-cycle work, so the two
            ;; counts are the same number. The processes arrive here as bare
            ;; ids anyway - they are two hops from the hydrated cycle and
            ;; their `:common/containing-entity` is not loaded - so the
            ;; mapping is not available to do it the other way.
            cycle-process-count (->> survey-batches
                                     (mapcat :survey-batch/survey-processes)
                                     (map (fn [survey-process]
                                            (or (:db/id survey-process) survey-process)))
                                     (distinct)
                                     (count))
            can-add-batch? (or (nil? profile-total)
                               (< cycle-process-count profile-total))
            adhoc-languages (batch-languages selected-batch*)
            adhoc-language (or (:adhoc-language @state)
                               (first adhoc-languages))
            adhoc-subjects (:adhoc-subjects @state)
            ;; Every language present needs a subject, or the recipients in
            ;; the one that was skipped get a blank one.
            adhoc-missing-subjects (->> adhoc-languages
                                        (remove (fn [lang]
                                                  (not (string/blank? (get adhoc-subjects lang)))))
                                        (vec))
            can-send-adhoc? (and (seq adhoc-languages)
                                 (empty? adhoc-missing-subjects))
            ;; Built by hand rather than through summarize-by, which sorts
            ;; alphabetically and so led with the cancellations. What is being
            ;; sent is the number that matters.
            reminder-summary (cond-> []
                               (pos? (count sending-reminders))
                               (conj {:label (loc/tr :survey-cycle-actions/will-send)
                                      :count (count sending-reminders)})

                               (pos? (count superseded-reminders))
                               (conj {:label (loc/tr :survey-cycle-actions/will-be-cancelled-short)
                                      :count (count superseded-reminders)}))
            active-tab (or (:active-tab @state) :invitations)
            ;; The batch itself is authoritative about what is still waiting to
            ;; go out - no preview required. The preview only refines that into
            ;; a number of emails, since a sendable process with no invitees
            ;; sends none.
            batch-sendable-count (count (sendable-processes selected-batch*))
            previewed? (seq (:preview-items @state))
            preview-email-count (->> (:preview-items @state)
                                     (filter (fn [item]
                                               (if (contains? item :sendable?)
                                                 (:sendable? item)
                                                 (= :survey-cycle.mass-send-status/ready
                                                    (:survey-cycle/mass-send-status item)))))
                                     (map (fn [item] (or (:invitee-count item) 0)))
                                     (reduce + 0))
            profiles-section
            [numbered-section
             {:index 1
              :title (loc/tr :survey-cycle-actions/step-choose-profiles)
              :action [:div
                       {:class "flex items-center gap-2"}
                       [text/info-text-xs {}
                        (loc/tr :survey-cycle-actions/recipient-count
                                [(count batch-recipients)])]
                       (when (seq batch-recipients)
                         [button/secondary-hollow
                          {:disabled? processing?
                           :on-click  (fn [_] (clear-recipients! state))}
                          (loc/tr :survey-cycle-actions/clear)])]}
             [:div
              {:class "flex flex-col gap-4"}
              [:div
               {:class "flex flex-col gap-2"}
               [labelled-input/view
                {:label     (loc/tr :survey-cycle-actions/search-profiles)
                 :data-type :data.type/text-simple
                 :value     (:profile-query @state)
                 :on-change (fn [value]
                              (swap! state assoc
                                     :profile-query value
                                     ;; Ticks belong to the result they were
                                     ;; made in. A new search is a new result.
                                     :staged-profile-ids #{})
                              (search-profiles! {:state          state
                                                 :survey-type-id survey-type-id
                                                 :query          value
                                                 :from-index     0}))}]
               [:div
                ;; A viewport of roughly a dozen rows: the set behind it can be
                ;; thousands, and only the loaded page is ever in the DOM.
                {:class "border border-solid border-base-light-border-paler rounded-lg overflow-hidden max-h-[240px] overflow-y-auto"}
                [:table
                 {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
                 (into [:tbody {:class "divide-y divide-solid divide-base-light-border-paler"}]
                       (if (seq candidate-profiles)
                         (map (fn [survey-profile]
                                [profile-row
                                 {:state          state
                                  :survey-profile survey-profile
                                  :added?         (contains? recipient-ids* (:db/id survey-profile))}])
                              candidate-profiles)
                         [[:tr
                           {:key "no-candidate-profiles"}
                           [:td
                            {:class "px-3 py-2 text-sm text-base-light-text-lighter"}
                            (loc/tr :survey-cycle-no-candidate-profiles)]]]))]]
               [:div
                {:class "flex flex-wrap items-center justify-between gap-3"}
                [text/info-text-xs {}
                 (loc/tr :survey-cycle-actions/showing-of
                         [(count candidate-profiles) (or profile-total 0)])]
                [:div
                 {:class "flex items-center gap-2"}
                 (when (< (count candidate-profiles) (or profile-total 0))
                   [button/secondary-hollow
                    {:disabled? (or processing? (:profiles-loading? @state))
                     :on-click  (fn [_]
                                  (search-profiles! {:state          state
                                                     :survey-type-id survey-type-id
                                                     :query          (:profile-query @state)
                                                     :from-index     (:profile-from @state)
                                                     :append?        true}))}
                    (loc/tr :survey-cycle-actions/load-more)])
                 ;; Adds everything the search matched, not just the loaded
                 ;; page, so a filter of thousands does not have to be paged
                 ;; through by hand. The server expands it and returns ids.
                 [button/secondary-hollow
                  {:disabled? (or processing?
                                  (zero? (or profile-total 0))
                                  search-fully-added?)
                   :on-click
                   (fn [_]
                     (request! state
                               :survey-cycle/matching-profiles
                               {:survey-cycle-id survey-cycle-id
                                :profile-filter  {:query (or (:profile-query @state) "")}}
                               (fn [body]
                                 (add-recipients! state body))))}
                  (loc/tr :survey-cycle-actions/add-all-matching [(or profile-total 0)])]
                 [button/primary-solid
                  {:disabled? (or processing? (empty? staged-profiles*))
                   :on-click  (fn [_] (add-recipients! state staged-profiles*))}
                  (loc/tr :survey-cycle-actions/add-selected [(count staged-profiles*)])]]]
               (when search-fully-added?
                 [text/info-text-xs {}
                  (loc/tr :survey-cycle-actions/all-results-added)])]

              [:div
               {:class "flex flex-col gap-2"}
               [text/label (loc/tr :survey-cycle-actions/recipients-heading)]
               (if (seq batch-recipients)
                 [:div
                  {:class "border border-solid border-base-light-border-paler rounded-lg overflow-hidden max-h-[200px] overflow-y-auto"}
                  [:table
                   {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
                   (into [:tbody {:class "divide-y divide-solid divide-base-light-border-paler"}]
                         (map (fn [recipient]
                                [:tr
                                 {:key   (str "batch-recipient-" (:db/id recipient))
                                  :class "*:px-3 *:py-2 *:text-sm"}
                                 [:td [:div {:class "font-medium"} (:label recipient)]]
                                 [:td
                                  {:class "w-10 text-right"}
                                  [button/square-secondary-open
                                   {:tooltip    (loc/tr :survey-cycle-actions/remove-recipient)
                                    :aria-label (loc/tr :survey-cycle-actions/remove-recipient)
                                    :disabled?  processing?
                                    :on-click   (fn [_]
                                                  (remove-recipient! state (:db/id recipient)))}
                                   [:i.fas.fa-xmark]]]])
                              batch-recipients))]]
                 [text/info-text (loc/tr :survey-cycle-actions/no-recipients-yet)])]]]

            sent-batch-record
            [:div
             {:class "flex flex-col gap-3"}
             [alert/warning {}
              (loc/tr :survey-cycle-actions/batch-already-sent
                      [(or (some-> (:survey-batch/sent-at selected-batch*)
                                   js/Date.
                                   .toLocaleString)
                           "-")])]
             [text/label (loc/tr :survey-cycle-actions/profiles-in-batch)]
             (cond
               (seq (:preview-items @state))
               [table-shell
                [(loc/tr :survey-cycle-actions/col-profile)
                 (loc/tr :survey-cycle-actions/col-template)
                 (loc/tr :survey-cycle-actions/col-deadline)
                 (loc/tr :survey-cycle-actions/col-recipients)
                 (loc/tr :survey-cycle-actions/col-status)]
                (map record-row (:preview-items @state))]

               processing?
               [text/info-text (loc/tr :loading)]

               :else
               [text/info-text (loc/tr :survey-cycle-actions/no-profiles-in-batch)])]

            invitations-tab
            [:div
             {:class "flex flex-col gap-4"}
             [batch-list
              {:survey-batches    batch-rows
               :selected-batch-id selected-batch-id
               :processing?       processing?
               :adding?           creating?
               :can-add-batch?    can-add-batch?
               :on-select!        (fn [survey-batch-id]
                                    (swap! state assoc :creating-batch? false)
                                    (select-batch!
                                     {:state           state
                                      :survey-cycle-id survey-cycle-id
                                      :survey-batches  survey-batches
                                      :survey-batch-id survey-batch-id}))
               :on-add!           (fn [_] (swap! state assoc :creating-batch? true))}]

             ;; Gated on the same condition as the button that opens it. A
             ;; form for a batch that cannot be created is worse than no form.
             (when (and creating? can-add-batch?)
               [:div
                {:class "flex flex-col gap-3 rounded-lg border border-solid border-base-light-border-paler p-3"}
                [text/label (loc/tr :survey-cycle-actions/create-batch-title)]
                [labelled-input/view
                 {:label     (loc/tr :survey-cycle-actions/batch-label)
                  :data-type :data.type/text-simple
                  :value     (:batch-label @state)
                  :on-change #(swap! state assoc :batch-label %)}]
                [labelled-input/view
                 {:label     (loc/tr :survey-cycle-actions/batch-deadline-override)
                  :data-type :data.type/datetime
                  :value     (:batch-deadline-override @state)
                  :on-change #(swap! state assoc :batch-deadline-override %)}]
                [labelled-input/view
                 {:label     (loc/tr :survey-cycle-actions/include-cc)
                  :data-type :data.type/boolean
                  :value     (boolean (:include-cc? @state))
                  :on-change #(swap! state assoc :include-cc? (boolean %))}]
                [text/info-text-xs {} (loc/tr :survey-cycle-actions/include-cc-help)]
                [:div
                 [button/primary-solid
                  {:disabled? (or processing? (not can-initialize?))
                   :on-click
                   (fn [_]
                     (request! state
                               :survey-cycle/initialize-batch
                               (cond-> {:survey-cycle-id survey-cycle-id
                                        :survey-batch/batch-label (:batch-label @state)
                                        ;; Sent whichever way it is set. The
                                        ;; batch keeps the decision, because
                                        ;; reminders go out later with nobody
                                        ;; there to make it again.
                                        :survey-batch/include-cc? (boolean (:include-cc? @state))
                                        ;; The recipient list is the batch. It
                                        ;; was assembled across however many
                                        ;; searches it took, so no filter can
                                        ;; describe it.
                                        :profile-ids (mapv :db/id batch-recipients)}
                                 (:batch-deadline-override @state)
                                 (assoc :survey-batch/batch-deadline-override
                                        (:batch-deadline-override @state)))
                               (fn [body]
                                 (swap! state assoc
                                        :preview-items (:items body)
                                        :initialized-batch-id (:survey-batch-id body)
                                        :selected-batch-id (:survey-batch-id body)
                                        :creating-batch? false
                                        :success (if (:survey-batch-id body)
                                                   (loc/tr :survey-cycle-actions/batch-created)
                                                   (loc/tr :survey-cycle-actions/batch-not-created))))))}
                  (loc/tr :survey-cycle-actions/initialize-batch)]]])

             (when-not record-only? profiles-section)

             (when record-only? sent-batch-record)

             (when-not sent?
               [numbered-section
                {:index 2
               :title (loc/tr :survey-cycle-actions/step-review)
               :action [button/secondary-hollow
                        {:disabled? (or processing? (not can-preview?))
                         :on-click
                         (fn [_]
                           (request! state
                                     :survey-cycle/get-batch-send-data
                                     (cond-> {:survey-cycle-id survey-cycle-id
                                              ;; Empty against a batch means
                                              ;; everything in it, which is what
                                              ;; Send Batch will send.
                                              :profile-ids []}
                                       selected-batch-id
                                       (assoc :survey-batch-id selected-batch-id))
                                     (fn [body]
                                       (swap! state assoc
                                              :preview-items body
                                              :success (loc/tr :survey-cycle-actions/preview-loaded)))))}
                        (loc/tr :survey-cycle-actions/refresh-preview)]}
              (if (seq (:preview-items @state))
                [:div
                 {:class "flex flex-col gap-3"}
                 [summary-badges {:summaries   preview-summary
                                  :empty-label (loc/tr :survey-cycle-actions/nothing-previewed-yet)}]
                 [table-shell
                  [(loc/tr :survey-cycle-actions/col-profile)
                   (loc/tr :survey-cycle-actions/col-template)
                   (loc/tr :survey-cycle-actions/col-deadline)
                   (loc/tr :survey-cycle-actions/col-recipients)
                   (loc/tr :survey-cycle-actions/col-reason)]
                  (map preview-row (:preview-items @state))]
                 ;; The batch froze its template, deadline and invitees when it
                 ;; was created, so say when that was rather than letting a
                 ;; later configuration change look as though it applied.
                 (when-let [created-on (:common/created-on selected-batch*)]
                   [alert/warning {}
                    (loc/tr :survey-cycle-actions/batch-frozen
                            [(.toLocaleString (js/Date. created-on))])])]
                  [text/info-text (loc/tr :survey-cycle-actions/no-preview-yet)])])

             (when-not sent?
               [numbered-section
                {:index 3
                 :title (loc/tr :survey-cycle-actions/step-send)}
              [:div
               {:class "flex flex-wrap items-center justify-between gap-3"}
               [text/info-text
                (cond
                  (zero? batch-sendable-count)
                  (loc/tr :survey-cycle-actions/send-nothing)

                  previewed?
                  (loc/tr :survey-cycle-actions/send-consequence [preview-email-count])

                  :else
                  (loc/tr :survey-cycle-actions/send-consequence-unpreviewed
                          [batch-sendable-count]))]
               [button/primary-solid
                {:disabled? (or processing? (not can-send-batch?))
                 :on-click (fn [_]
                             (request! state
                                       :survey-cycle/send-batch
                                       {:survey-cycle-id survey-cycle-id
                                        :survey-batch-id selected-batch-id}
                                       (fn [_body]
                                         (swap! state assoc
                                                :success (loc/tr :survey-cycle-actions/batch-was-sent)))))}
                  (loc/tr :survey-cycle-actions/send-batch)]]])]

            reminders-tab
            [:div
             {:class "flex flex-col gap-4"}
             [numbered-section
              {:index 1
               :title (loc/tr :survey-cycle-actions/step-find-due)
               :action [button/secondary-hollow
                        {:disabled? processing?
                         :on-click
                         (fn [_]
                           (request! state
                                     :survey-cycle/get-due-reminders
                                     (reminder-scope
                                      {:survey-cycle-id  survey-cycle-id
                                       :survey-batch-id  selected-batch-id
                                       :batch-recipients batch-recipients})
                                     (fn [body]
                                       (swap! state assoc
                                              :due-reminder-items body
                                              :success (loc/tr :survey-cycle-actions/due-reminders-loaded)))))}
                        (loc/tr :survey-cycle-actions/preview-due)]}
              [summary-badges {:summaries   reminder-summary
                               :empty-label (if reminders-checked?
                                              (loc/tr :survey-cycle-actions/nothing-due)
                                              (loc/tr :survey-cycle-actions/not-checked-yet))}]]

             [numbered-section
              {:index 2
               :title (loc/tr :survey-cycle-actions/step-review-reminders)
               ;; The section stays - it is where the answer to "who would be
               ;; reminded" is - but scheduling is only offered when there is
               ;; something to schedule.
               :action (when (pos? schedulable-count)
                         [button/secondary-hollow
                          {:disabled? processing?
                           :on-click
                           (fn [_]
                             (request! state
                                       :survey-cycle/materialize-due-reminders
                                       (reminder-scope
                                        {:survey-cycle-id  survey-cycle-id
                                         :survey-batch-id  selected-batch-id
                                         :batch-recipients batch-recipients})
                                       (fn [body]
                                         (swap! state assoc
                                                :due-reminder-items (:items body)
                                                :success (loc/tr :survey-cycle-actions/reminders-scheduled
                                                                 [(count (:survey-reminder-ids body))])))))}
                          (loc/tr :survey-cycle-actions/materialize-due)])}
              (if (seq (:due-reminder-items @state))
                [:div
                 {:class "border border-solid border-base-light-border-paler rounded-lg overflow-hidden"}
                 [:table
                  {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
                  (into [:tbody {:class "divide-y divide-solid divide-base-light-border-paler"}]
                        (map due-reminder-row (:due-reminder-items @state)))]]
                [text/info-text (loc/tr :survey-cycle-actions/no-due-reminders)])]

             ;; Nothing to send means no step 3 at all. Offering Send next to
             ;; a count of zero invited the reading that something would go
             ;; out, and the automatic button beside it sends the same empty
             ;; set, so neither belongs here until there is something to send.
             (when (pos? due-reminder-count)
               [numbered-section
                {:index 3
                 :title (loc/tr :survey-cycle-actions/step-send-reminders)}
              [:div
               {:class "flex flex-col gap-2"}
               [:div
                {:class "flex flex-wrap items-center justify-between gap-3"}
                [text/info-text
                 (if (pos? superseded-count)
                   (loc/tr :survey-cycle-actions/reminder-consequence-superseded
                           [due-reminder-count superseded-count])
                   (loc/tr :survey-cycle-actions/reminder-consequence
                           [due-reminder-count]))]
                [:div
                 {:class "flex flex-wrap gap-2"}
                 [button/primary-solid
                  {:disabled? processing?
                   :on-click
                   (fn [_]
                     (request! state
                               :survey-cycle/send-due-reminders
                               (reminder-scope
                                {:survey-cycle-id  survey-cycle-id
                                 :survey-batch-id  selected-batch-id
                                 :batch-recipients batch-recipients})
                               (fn [body]
                                 (swap! state assoc
                                        :success (loc/tr :survey-cycle-actions/reminders-sent
                                                         [(count (:survey-reminder-ids body))])))))}
                  (loc/tr :survey-cycle-actions/send-due)]
                 [button/secondary-hollow
                  {:disabled? (or processing? (not automatic-reminders?))
                   :tooltip (when-not automatic-reminders?
                              (loc/tr :survey-cycle-actions/automatic-disabled))
                   :on-click
                   (fn [_]
                     (request! state
                               :survey-cycle/send-automatic-reminders
                               (cond-> {:survey-cycle-id survey-cycle-id}
                                 selected-batch-id
                                 (assoc :survey-batch-id selected-batch-id))
                               (fn [body]
                                 (swap! state assoc
                                        :success (loc/tr :survey-cycle-actions/reminders-sent
                                                         [(count (:survey-reminder-ids body))])))))}
                  (loc/tr :survey-cycle-actions/send-automatic)]]]
                 (when-not automatic-reminders?
                   [text/info-text-xs {}
                    (loc/tr :survey-cycle-actions/automatic-disabled)])]])]

            adhoc-reminders-tab
            ;; Its own tab rather than a fourth step beside the scheduled
            ;; ones. Those answer what the schedule owes; this answers "chase
            ;; these people now", and is available whether or not anything is
            ;; due - which is the whole point of it.
            [:div
             {:class "flex flex-col gap-3 rounded-lg border border-solid border-base-light-border-paler p-3"}
             [text/label (loc/tr :survey-cycle-actions/adhoc-title)]
             [text/info-text-xs {}
              (loc/tr :survey-cycle-actions/adhoc-help)]
             (if-not (seq adhoc-languages)
               [text/info-text (loc/tr :survey-cycle-actions/adhoc-no-recipients)]
               [:<>
                (when (< 1 (count adhoc-languages))
                  [tab-strip/view
                   {:selected-tab adhoc-language
                    :tabs         (mapv (fn [lang]
                                          {:value lang
                                           :label (languages/get-label lang)})
                                        adhoc-languages)
                    :on-change!   #(swap! state assoc :adhoc-language %)}])
                ^{:key (str "adhoc-subject-" adhoc-language)}
                [labelled-input/view
                 {:label      (loc/tr :email/subject)
                  :mandatory? true
                  :data-type  :data.type/text-simple
                  :value      (get adhoc-subjects adhoc-language)
                  :on-change  (fn [new-value]
                                (swap! state assoc-in
                                       [:adhoc-subjects adhoc-language] new-value))}]
                ^{:key (str "adhoc-body-1-" adhoc-language)}
                [labelled-input/view
                 {:label     (loc/tr :email-body-part-1)
                  :data-type :data.type/text-multiline
                  :value     (get (:adhoc-bodies-1 @state) adhoc-language)
                  :on-change (fn [new-value]
                               (swap! state assoc-in
                                      [:adhoc-bodies-1 adhoc-language] new-value))}]
                ^{:key (str "adhoc-body-2-" adhoc-language)}
                [labelled-input/view
                 {:label     (loc/tr :email-body-part-2)
                  :data-type :data.type/text-multiline
                  :value     (get (:adhoc-bodies-2 @state) adhoc-language)
                  :on-change (fn [new-value]
                               (swap! state assoc-in
                                      [:adhoc-bodies-2 adhoc-language] new-value))}]
                (when (seq adhoc-missing-subjects)
                  [text/info-text-xs {}
                   (loc/tr :survey-cycle-actions/adhoc-missing-subject
                           [(string/join ", " (map languages/get-label adhoc-missing-subjects))])])
                [:div
                 [button/primary-solid
                  {:disabled? (or processing? (not can-send-adhoc?))
                   :on-click
                   (fn [_]
                     (request! state
                               :survey-cycle/send-adhoc-reminders
                               (merge (reminder-scope
                                       {:survey-cycle-id  survey-cycle-id
                                        :survey-batch-id  selected-batch-id
                                        :batch-recipients batch-recipients})
                                      {:subjects (:adhoc-subjects @state)
                                       :bodies-1 (:adhoc-bodies-1 @state)
                                       :bodies-2 (:adhoc-bodies-2 @state)})
                               (fn [body]
                                 (swap! state assoc
                                        :adhoc-subjects nil
                                        :adhoc-bodies-1 nil
                                        :adhoc-bodies-2 nil
                                        :success (loc/tr :survey-cycle-actions/adhoc-sent
                                                         [(count (:survey-reminder-ids body))])))))}
                  (loc/tr :survey-cycle-actions/adhoc-send)]]])]

            body-content
            [:div
             ;; `tw2` scopes the prefixed utility rules (tailwind.tw2.config.js
             ;; emits them as `.tw2 .tw2-*`) so tw2-styled components like the
             ;; profile checkboxes render correctly inside this legacy modal.
             {:class "tw2 flex flex-col gap-4"}
             ;; Above the tabs, not inside the invitations one: both tabs are
             ;; scoped to a cycle, and opened from the listing neither of them
             ;; has been given one. Only the listing shows it - opened from a
             ;; cycle row the cycle is what the modal is about and cannot be
             ;; swapped underneath it.
             (when-not cycle-fixed?
               [labelled-input/view
                {:label       (loc/tr :survey-cycle-actions/cycle)
                 :mandatory?  true
                 :editor-type :editor.type/select
                 :data-type   :data.type/ref-single
                 :options     (sendable-cycle-options (:sendable-cycles @state))
                 :value       (:chosen-cycle-id @state)
                 :on-change   (fn [new-value & _]
                                ;; The label comes from the summary, not from
                                ;; the session - the entity is not there until
                                ;; hydrate-cycle! puts it there.
                                (let [chosen (cu/find-first-by :db/id new-value
                                                               (:sendable-cycles @state))]
                                  (swap! state assoc
                                         :chosen-cycle-id new-value
                                         :batch-label (default-batch-label
                                                       chosen
                                                       (survey-batches-for-cycle
                                                        (session/get-entity new-value)))
                                         :preview-items nil
                                         :initialized-batch-id nil
                                         :selected-batch-id nil
                                         :due-reminder-items nil)
                                  (hydrate-cycle! {:state           state
                                                   :survey-cycle-id new-value})))}])
             [tab-strip/view
              {:tabs         [{:value :invitations
                               :label (loc/tr :survey-cycle-actions/tab-invitations)}
                              {:value :reminders
                               :label (loc/tr :survey-cycle-actions/tab-reminders)}
                              {:value :adhoc-reminders
                               :label (loc/tr :survey-cycle-actions/tab-adhoc-reminders)}]
               :selected-tab active-tab
               :on-change!   #(swap! state assoc :active-tab %)}]
             (when-let [success (:success @state)]
               [:div {:class "text-sm text-success"} success])
             (when-let [error (:error @state)]
               [:div {:class "text-sm text-danger"} error])
             (case active-tab
               :reminders       reminders-tab
               :adhoc-reminders adhoc-reminders-tab
               invitations-tab)]]
        [modal/view
         {:visible? (and shown? topmost?)
          :hide! close-modal!
          :backdrop "static"
          :close-button? true
          :size "1100px"}
         (str "Survey Cycle Actions: " (:survey-cycle/cycle-label survey-cycle))
         body-content
         [modal/right-single-button
          nil
          processing?
          (fn [_]
            (close-modal!))
          (if processing? (loc/tr :processing) (loc/tr :close))
          nil]]))))
